import { API_URL } from './env'
import { getSessionId } from './session-id'

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  headers?: Record<string, string>
  /** Send anonymous-session-id header (used for anonymous document ops). */
  withSessionId?: boolean
  /** Skip auto-refresh of CSRF on 419 (used by csrf endpoint itself). */
  skipCsrfRefresh?: boolean
}

export class ApiError extends Error {
  status: number
  details: unknown
  errors?: Record<string, string[]>

  constructor(message: string, status: number, details?: unknown, errors?: Record<string, string[]>) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.details = details
    this.errors = errors
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]+)'))
  return match ? decodeURIComponent(match[1]) : null
}

let csrfPromise: Promise<void> | null = null

/**
 * Hit Laravel's `/sanctum/csrf-cookie` once to obtain the `XSRF-TOKEN` cookie.
 * Subsequent state-changing requests include it as `X-XSRF-TOKEN`.
 */
export function ensureCsrf(): Promise<void> {
  if (readCookie('XSRF-TOKEN')) return Promise.resolve()
  if (!csrfPromise) {
    csrfPromise = fetch(`${API_URL}/sanctum/csrf-cookie`, {
      credentials: 'include',
    })
      .then((r) => {
        if (!r.ok) throw new Error(`CSRF init failed: ${r.status}`)
      })
      .finally(() => {
        csrfPromise = null
      })
  }
  return csrfPromise
}

const UNSAFE = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

async function parseError(response: Response): Promise<never> {
  const ct = response.headers.get('Content-Type') || ''
  if (ct.includes('application/json')) {
    const body = (await response.json().catch(() => ({}))) as {
      message?: string
      errors?: Record<string, string[]>
      details?: unknown
    }
    throw new ApiError(body.message || response.statusText, response.status, body.details, body.errors)
  }
  const text = await response.text().catch(() => '')
  throw new ApiError(text || response.statusText, response.status)
}

export async function request<T = unknown>(
  url: string,
  opts: RequestOptions = {},
): Promise<{ status: number; data: T | null }> {
  const { method = 'GET', body, headers = {}, withSessionId, skipCsrfRefresh } = opts

  if (UNSAFE.has(method) && !skipCsrfRefresh) {
    await ensureCsrf()
  }

  const finalHeaders: Record<string, string> = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    ...headers,
  }

  if (body !== undefined && !(body instanceof FormData)) {
    finalHeaders['Content-Type'] = 'application/json'
  }

  if (UNSAFE.has(method)) {
    const xsrf = readCookie('XSRF-TOKEN')
    if (xsrf) finalHeaders['X-XSRF-TOKEN'] = xsrf
  }

  if (withSessionId) {
    finalHeaders['X-Session-Id'] = getSessionId()
  }

  const init: RequestInit = {
    method,
    headers: finalHeaders,
    credentials: 'include',
  }

  if (body !== undefined) {
    init.body = body instanceof FormData ? body : JSON.stringify(body)
  }

  let response: Response
  try {
    response = await fetch(`${API_URL}${url}`, init)
  } catch {
    throw new ApiError('Network error. Please check your internet connection.', 0)
  }

  // Session expired or CSRF token mismatch — refresh once and retry.
  if (response.status === 419 && !skipCsrfRefresh) {
    csrfPromise = null
    await ensureCsrf()
    return request<T>(url, { ...opts, skipCsrfRefresh: true })
  }

  if (!response.ok) {
    await parseError(response)
  }

  const text = await response.text()
  const data = text ? (JSON.parse(text) as T) : null
  return { status: response.status, data }
}
